import { defineMeta } from '@/registry/types';

export const tableMeta = defineMeta({
  name: 'Table',
  level: 'organism',
  category: 'data-display',
  classification: 'mui-wrapper',
  description:
    'The standard list view. With only `columns` and `data` it renders a bordered card with a recessed uppercase header, comfortable rows and hover feedback. Toolbar, pagination, sorting and column menus are off unless asked for.',
  baseLibrary: 'material-react-table',
  muiEquivalent: 'Table (enhanced)',
  muiDocs: 'https://www.material-react-table.com/',
  a2uiEquivalent: 'DataTable',
  importPath: '@/organisms/Table',
  requiredProps: ['columns', 'data'],
  optionalProps: [
    'onRowClick',
    'selectedRowId',
    'isRowDimmed',
    'loading',
    'empty',
    'sticky',
    'maxHeight',
    'density',
    'toolbar',
    'pagination',
    'sortable',
    'columnActions',
    'surface',
    'bordered',
  ],
  supportedStates: [
    'default',
    'hover',
    'selected',
    'dimmed',
    'loading',
    'empty',
    'sorted',
    'paginated',
    'sticky',
  ],
  designTokens: ['colors', 'radius', 'spacing', 'typography'],
  accessibility: [
    'Renders a semantic table; write column headers that describe the data, not the widget.',
    '`onRowClick` wires Enter/Space, tabIndex and aria-selected - do not hand-roll row interaction.',
    'When a row is the only way to reach a record, the row must be clickable, not just an icon inside it.',
  ],
  usageExamples: [
    '<DataTable columns={columns} data={rows} />',
    '<DataTable columns={columns} data={rows} onRowClick={(row) => open(row.id)} selectedRowId={selectedId} />',
    '<DataTable columns={columns} data={rows} loading={isFetching} empty={<EmptyState title="No detection rules yet" description="Import a pack or write your first rule." />} />',
    '<DataTable columns={columns} data={rows} toolbar pagination sortable sticky maxHeight={420} />',
    '<Card variant="data" title="Historical Cases"><DataTable columns={columns} data={rows} bordered={false} surface="transparent" /></Card>',
  ],
  rules: [
    'On a surface (page body, panel, dialog) keep the default bordered table so the outline is the structural edge. Header fill (~1.08:1, no underline unless `sticky`) and row rules (`--ds-separator`, ~1.12:1) track the host.',
    'Inside a Card pass `bordered={false} surface="transparent"`. The card opens the next rung and the table spans its width so the header fill is not inset; 24px cell padding keeps columns aligned with the title.',
  ],
  antiPatterns: [
    'Do not restyle through `muiTablePaperProps` / `muiTableHeadCellProps` descendant selectors - the props above cover it, and column-level cell props silently clobber table-level ones.',
    'Do not wrap the table in a Box to get a border or radius - it is bordered by default, and `bordered={false}` turns it off.',
    'Do not leave the table outlined inside a Card - that double-codes the edge the card already provides.',
    'Do not hardcode hex for surfaces or rules - the table inherits its host rung (`--ds-bg`); header and hover are derived from it; outline uses `--ds-border`, row rules use `--ds-separator`.',
    'Do not pass `enableTopToolbar={false} enablePagination={false} enableSorting={false} enableColumnActions={false}` - those are already the defaults.',
    'Do not build a list view out of a CSS grid of Boxes because the table "felt heavy" - it is a table, and grids lose semantics, sorting and keyboard support.',
    'Do not use for a handful of static key/value pairs (use a definition list or Card).',
  ],
  llmSafe: true,
  whenToUse: ['Any list of records the user scans, filters or opens'],
  whenNotToUse: ['A handful of static rows with no interaction'],
});

export default tableMeta;
